// src/pages/Templates.jsx

import React, { useState, useEffect } from 'react';
import {
  FiRefreshCw,
  FiPlus,
  FiAlertTriangle,
  FiAlertCircle,
  FiSearch,
  FiX,
  FiFileText,
  FiLoader,
  FiSave,
  FiEye,
  FiTrash
} from 'react-icons/fi';
import Cookies from "js-cookie";

/** AccordionSection – reusable collapsible panel */
function AccordionSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-600 rounded-lg overflow-hidden mb-4">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex justify-between items-center px-4 py-2 bg-gray-700 text-gray-100 hover:bg-gray-600"
      >
        <span className="font-medium">{title}</span>
        <svg
          className={`w-5 h-5 transform transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="p-4 bg-gray-800">{children}</div>}
    </div>
  );
}

export default function Templates() {
  // Redirect to login if token missing
  useEffect(() => {
    const token = Cookies.get("authToken");
    if (!token) {
      window.location.href = "/";
    }
  }, []);

  // Templates state
  const [templates, setTemplates]               = useState([]);
  const [pendingTemplates, setPendingTemplates] = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);

  // Notification categories
  const [categories, setCategories] = useState({});

  // Modal & form state
  const [showModal, setShowModal]               = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [deleteTarget, setDeleteTarget]         = useState(null);

  // View-details modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewTemplate, setViewTemplate]   = useState(null);

  // Form fields
  const [templateName, setTemplateName]           = useState('');
  const [notifCategory, setNotifCategory]         = useState('');
  const [waCategory, setWaCategory]               = useState('');
  const [language, setLanguage]                   = useState('en_US');
  const [headerType, setHeaderType]               = useState('none');
  const [headerText, setHeaderText]               = useState('');
  const [headerImage, setHeaderImage]             = useState('');
  const [bodyText, setBodyText]                   = useState('');
  const [footerText, setFooterText]               = useState('');
  const [placeholders, setPlaceholders]           = useState([]);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [submitting, setSubmitting]               = useState(false);

  useEffect(() => {
    loadCategories();
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getAuthHeaders(extra = {}) {
    const token = Cookies.get("authToken");
    const base = token ? { Authorization: `Bearer ${token}` } : {};
    return { ...base, ...extra };
  }

  function loadCategories() {
    setError(null);
    fetch('/api/api/notification-categories', {
      headers: getAuthHeaders()
    })
      .then(res => res.ok ? res.json() : res.text().then(t => { throw new Error(t); }))
      .then(data => {
        if (data && data.success) setCategories(data.categories || {});
        else setCategories({});
      })
      .catch(() => {/* ignore silently */});
  }

  function loadTemplates() {
    setLoading(true);
    setError(null);

    const headers = getAuthHeaders();

    Promise.all([
      fetch('/api/api/templates?status=PENDING', { headers }),
      fetch('/api/api/templates?status=APPROVED', { headers }),
      fetch('/api/api/templates?status=REJECTED', { headers }),
    ])
      .then(async ([pRes, aRes, rRes]) => {
        if (!pRes.ok || !aRes.ok || !rRes.ok) {
          const texts = await Promise.all([pRes.text(), aRes.text(), rRes.text()]);
          throw new Error(`P:${texts[0]} | A:${texts[1]} | R:${texts[2]}`);
        }
        const [pJson, aJson, rJson] = await Promise.all([pRes.json(), aRes.json(), rRes.json()]);
        return {
          pending:  Array.isArray(pJson.templates)  ? pJson.templates  : (Array.isArray(pJson.data)  ? pJson.data  : []),
          approved: Array.isArray(aJson.templates)  ? aJson.templates  : (Array.isArray(aJson.data)  ? aJson.data  : []),
          rejected: Array.isArray(rJson.templates)  ? rJson.templates  : (Array.isArray(rJson.data)  ? rJson.data  : []),
        };
      })
      .then(({ pending, approved, rejected }) => {
        setPendingTemplates(pending || []);
        setTemplates([...(approved || []), ...(rejected || [])]);
      })
      .catch(err => setError(`Failed to load templates: ${err.message}`))
      .finally(() => setLoading(false));
  }

  function scanPlaceholders() {
    const re = /\{\{(\d+)\}\}/g;
    const found = new Set();
    let m;
    while ((m = re.exec(bodyText)) !== null) found.add(m[1]);
    const phs = Array.from(found).sort((a, b) => +a - +b);
    setPlaceholders(phs);
    setPlaceholderValues(vals => {
      const out = { ...vals };
      phs.forEach(i => { if (!(i in out)) out[i] = ''; });
      return out;
    });
  }

  function resetForm() {
    setTemplateName('');
    setNotifCategory('');
    setWaCategory('');
    setLanguage('en_US');
    setHeaderType('none');
    setHeaderText('');
    setHeaderImage('');
    setBodyText('');
    setFooterText('');
    setPlaceholders([]);
    setPlaceholderValues({});
  }

  // function handleEdit(tpl) {
  //   resetForm();
  //   setTemplateName(tpl.name || '');
  //   setNotifCategory('');
  //   setWaCategory(tpl.category || '');
  //   setLanguage(tpl.language || 'en_US');

  //   const hdr = Array.isArray(tpl.components) ? tpl.components.find(c => c.type === 'HEADER') : null;
  //   if (hdr) {
  //     if (hdr.format === 'TEXT') {
  //       setHeaderType('text');
  //       setHeaderText(hdr.text || '');
  //     } else if (hdr.format === 'IMAGE') {
  //       setHeaderType('image');
  //       setHeaderImage(hdr.image?.link || '');
  //     }
  //   }

  //   const bdy = Array.isArray(tpl.components) ? tpl.components.find(c => c.type === 'BODY') : null;
  //   setBodyText(bdy?.text || '');

  //   const ftr = Array.isArray(tpl.components) ? tpl.components.find(c => c.type === 'FOOTER') : null;
  //   setFooterText(ftr?.text || '');

  //   const re = /\{\{(\d+)\}\}/g;
  //   const found = new Set();
  //   let m2;
  //   while ((m2 = re.exec(bdy?.text || '')) !== null) found.add(m2[1]);
  //   const phs = Array.from(found).sort((a, b) => +a - +b);
  //   setPlaceholders(phs);
  //   const pv = {};
  //   phs.forEach(idx => { pv[idx] = ''; });
  //   setPlaceholderValues(pv);

  //   setShowModal(true);
  // }

  function handleSubmit(e) {
    e.preventDefault();
    if (!templateName || !notifCategory || !waCategory || !bodyText) return;
    setSubmitting(true);

    const re = /\{\{(\d+)\}\}/g;
    const phsSet = new Set();
    let match;
    while ((match = re.exec(bodyText)) !== null) {
      phsSet.add(match[1]);
    }
    const phs = Array.from(phsSet).sort((a, b) => +a - +b);

    const comps = [];
    if (headerType === 'text' && headerText) {
      comps.push({ type: 'HEADER', format: 'TEXT', text: headerText });
    } else if (headerType === 'image' && headerImage) {
      comps.push({ type: 'HEADER', format: 'IMAGE', image: { link: headerImage } });
    }

    const bodyComp = { type: 'BODY', text: bodyText };
    if (phs.length > 0) {
      // example should be an array of arrays matching template parameters
      bodyComp.example = { body_text: [ phs.map(idx => placeholderValues[idx] || '') ] };
    }
    comps.push(bodyComp);

    if (footerText) {
      comps.push({ type: 'FOOTER', text: footerText });
    }

    const payload = {
      name: templateName,
      language,
      category: waCategory,
      components: comps
    };

    fetch('/api/api/templates', {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload)
    })
      .then(res =>
        res.ok
          ? res.json()
          : res.text().then(t => { throw new Error(t); })
      )
      .then(data => {
        setSubmitting(false);
        if (data && data.success) {
          setShowModal(false);
          resetForm();
          loadTemplates();
          setShowSuccessModal(true);
        } else {
          alert(data?.details || data?.error || 'Submission failed');
        }
      })
      .catch(err => {
        setSubmitting(false);
        alert('Network/API error: ' + err.message);
      });
  }

  function onDeleteClick(name) {
    setDeleteTarget(name);
    setShowDeleteModal(true);
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    fetch(`/api/api/templates/${encodeURIComponent(deleteTarget)}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    })
      .then(res =>
        res.ok
          ? res.json()
          : res.text().then(t => { throw new Error(t); })
      )
      .then(() => {
        setShowDeleteModal(false);
        loadTemplates();
      })
      .catch(err => {
        setShowDeleteModal(false);
        alert('Delete failed: ' + err.message);
      });
  }

  function handleView(tpl) {
    setViewTemplate(tpl);
    setShowViewModal(true);
  }

  const displayed = [...(pendingTemplates || []), ...(templates || [])];

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mx-auto" style={{ maxWidth: '1800px' }}>

          {/* Header + Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">WhatsApp Template Management</h1>
              <p className="text-gray-600 mt-1">Manage message templates</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadTemplates}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <FiRefreshCw className="w-5 h-5" /> Refresh
              </button>
              <button
                onClick={() => { resetForm(); setShowModal(true); }}
                className="flex items-center gap-2 px-4 py-2 text-white bg-[#581745] rounded-lg hover:bg-[#1fa2c9]"
              >
                <FiPlus className="w-5 h-5" /> Create Template
              </button>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex flex-col justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-4 border-blue-600 border-gray-200"></div>
              <p className="mt-4 text-gray-600">Loading templates…</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg mb-4">
              <div className="flex items-center gap-2 text-red-700">
                <FiAlertTriangle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && displayed.length === 0 && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-4">
              <div className="flex items-center gap-2 text-yellow-700">
                <FiAlertCircle className="w-5 h-5" />
                <span>No templates found. Create one to get started.</span>
              </div>
            </div>
          )}

          {/* Cards Grid */}
          {!loading && !error && displayed.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayed.map(t => (
                <div
                  key={t.id || t.name}
                  className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col justify-between hover:shadow-lg transition-shadow"
                >
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">{t.name}</h3>
                      <span
                        className={`px-2 py-0.5 text-sm rounded-full ${
                          t.status === 'APPROVED'
                            ? 'bg-green-100 text-green-800'
                            : t.status === 'REJECTED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {t.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs mb-4">
                      <span className="bg-gray-100 text-gray-800 px-2 py-0.5 rounded">{t.category}</span>
                      <span className="bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                        {t.language?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm line-clamp-3 mb-6">
                      {Array.isArray(t.components) ? (t.components.find(c => (c.type || '').toLowerCase() === 'body')?.text || 'No preview available') : 'No preview available'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleView(t)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded text-gray-700"
                    >
                      <FiEye className="w-4 h-4" /> View
                    </button>
                    <button
                      onClick={() => onDeleteClick(t.name)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-50 hover:bg-red-100 rounded text-red-800"
                    >
                      <FiTrash className="w-4 h-4" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-gray-800 text-gray-100 rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">

                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <FiFileText className="w-6 h-6 text-white" />
                    {templateName ? 'Edit WhatsApp Template' : 'Create New WhatsApp Template'}
                  </h3>
                  <button onClick={() => setShowModal(false)}>
                    <FiX className="w-6 h-6 text-gray-400 hover:text-white transition-colors" />
                  </button>
                </div>

                {/* Modal Body */}
                <form
                  id="templateForm"
                  onSubmit={handleSubmit}
                  className="p-6 space-y-6 overflow-y-auto flex-1"
                >
                  {/* Notification Category */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium">Notification Category</label>
                    <select
                      value={notifCategory}
                      onChange={e => setNotifCategory(e.target.value)}
                      className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="" disabled>Select a notification type</option>
                      {Object.entries(categories || {}).map(([key]) => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400">
                      Select a notification category to automatically populate template details.
                    </p>
                  </div>

                  {/* Template Name */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium">Template Name</label>
                    <input
                      type="text"
                      value={templateName}
                      onChange={e => setTemplateName(e.target.value)}
                      placeholder="Enter template name"
                      className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  {/* WhatsApp Category & Language */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium">WhatsApp Category</label>
                      <select
                        value={waCategory}
                        onChange={e => setWaCategory(e.target.value)}
                        className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="" disabled>Select a category</option>
                        <option value="UTILITY">UTILITY</option>
                        <option value="MARKETING">MARKETING</option>
                        <option value="AUTHENTICATION">AUTHENTICATION</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-medium">Language</label>
                      <select
                        value={language}
                        onChange={e => setLanguage(e.target.value)}
                        className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="en_US">English (US)</option>
                        <option value="es">Spanish</option>
                        <option value="pt">Portuguese</option>
                      </select>
                    </div>
                  </div>

                  {/* Header (Optional) */}
                  <AccordionSection title="Header (Optional)" defaultOpen={false}>
                    <div className="space-y-2">
                      <label className="block text-sm">Header Type</label>
                      <select
                        value={headerType}
                        onChange={e => setHeaderType(e.target.value)}
                        className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="none">No Header</option>
                        <option value="text">Text Header</option>
                        <option value="image">Image Header</option>
                      </select>
                      {headerType === 'text' && (
                        <input
                          type="text"
                          value={headerText}
                          onChange={e => setHeaderText(e.target.value)}
                          placeholder="Header text..."
                          className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-blue-500"
                        />
                      )}
                      {headerType === 'image' && (
                        <input
                          type="url"
                          value={headerImage}
                          onChange={e => setHeaderImage(e.target.value)}
                          placeholder="Image URL..."
                          className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-blue-500"
                        />
                      )}
                    </div>
                  </AccordionSection>

                  {/* Body (Required) */}
                  <AccordionSection title="Body (Required)" defaultOpen={true}>
                    <div className="space-y-2">
                      <label className="block text-sm">Body Text</label>
                      <textarea
                        value={bodyText}
                        onChange={e => setBodyText(e.target.value)}
                        rows={4}
                        placeholder="e.g., Hi {{1}}, your order ({{2}}) has been confirmed!"
                        className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <p className="text-xs text-gray-400">Include placeholders as needed.</p>
                      <button
                        type="button"
                        onClick={scanPlaceholders}
                        className="flex items-center gap-2 text-blue-400 hover:text-blue-300"
                      >
                        <FiSearch className="w-5 h-5" /> Scan Placeholders
                      </button>
                      {placeholders.length > 0 && (
                        <div className="bg-gray-700 p-4 rounded-lg space-y-2">
                          {placeholders.map(idx => (
                            <div key={idx} className="space-y-1">
                              <label className="text-xs">
                                Value for {'{{'}{idx}{'}}'}
                              </label>
                              <input
                                type="text"
                                value={placeholderValues[idx] || ''}
                                onChange={e =>
                                  setPlaceholderValues(v => ({
                                    ...v,
                                    [idx]: e.target.value
                                  }))
                                }
                                className="w-full bg-gray-600 text-gray-200 px-3 py-1 rounded-lg border border-gray-500 focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </AccordionSection>

                  {/* Footer (Optional) */}
                  <AccordionSection title="Footer (Optional)" defaultOpen={false}>
                    <input
                      type="text"
                      value={footerText}
                      onChange={e => setFooterText(e.target.value)}
                      placeholder="Footer text..."
                      className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-lg border border-gray-600 focus:ring-2 focus:ring-blue-500"
                    />
                  </AccordionSection>

                  {/* Preview */}
                  <div className="bg-blue-50 p-4 rounded-lg mt-4">
                    <div className="text-sm font-semibold mb-2 text-gray-900">Template Preview</div>
                    <div className="bg-gray-800 text-gray-200 p-2 rounded">
                      Body:{' '}
                      {bodyText || <span className="text-gray-500">Your template body will appear here</span>}
                    </div>
                  </div>
                </form>

                {/* Modal Actions */}
                <div className="flex-shrink-0 flex justify-end gap-3 pt-4 border-t border-gray-700 px-6 py-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="templateForm"
                    disabled={submitting}
                    className="px-4 py-2 rounded-lg flex items-center gap-2"
                    style={{ backgroundColor: '#581745' }}
                  >
                    {submitting ? (
                      <>
                        <FiLoader className="animate-spin w-5 h-5" /> Submitting…
                      </>
                    ) : (
                      <>
                        <FiSave className="w-5 h-5" /> Submit Template
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                  <h3 className="text-lg	font-semibold text-gray-900">Confirm Deletion</h3>
                  <button onClick={() => setShowDeleteModal(false)}>
                    <FiX className="w-6 h-6 text-gray-500 hover:text-gray-800" />
                  </button>
                </div>
                <div className="p-6">
                  <p className="text-gray-800">
                    Are you sure you want to delete template "<span className="font-medium">{deleteTarget}</span>"?
                  </p>
                </div>
                <div className="flex justify-end gap-3 px-6 py-4 border-t">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-4	py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4	py-2 bg-red-600 rounded-lg hover:bg-red-700 text-white"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Success Modal */}
          {showSuccessModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Success
                  </h3>
                  <button onClick={() => setShowSuccessModal(false)}>
                    <FiX className="w-6 h-6 text-gray-500 hover:text-gray-800" />
                  </button>
                </div>
                <div className="p-6">
                  <p className="text-gray-800">
                    Your template has been submitted for approval.
                  </p>
                </div>
                <div className="flex justify-end gap-3 px-6 py-4 border-t">
                  <button
                    onClick={() => setShowSuccessModal(false)}
                    className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 text-white"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* View Details Modal */}
          {showViewModal && viewTemplate && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-auto max-h-full">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Template Details</h3>
                  <button onClick={() => setShowViewModal(false)}>
                    <FiX className="w-6 h-6 text-gray-500 hover:text-gray-800" />
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  <p><strong>Name:</strong> {viewTemplate.name}</p>
                  {viewTemplate.id && <p><strong>ID:</strong> {viewTemplate.id}</p>}
                  <p>
                    <strong>Status:</strong>{' '}
                    <span className={`px-2 py-0.5 rounded-full ${
                      viewTemplate.status === 'APPROVED'
                        ? 'bg-green-100 text-green-800'
                        : viewTemplate.status === 'REJECTED'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {viewTemplate.status}
                    </span>
                  </p>
                  <p><strong>Category:</strong> {viewTemplate.category}</p>
                  <p><strong>Language:</strong> {viewTemplate.language?.toUpperCase()}</p>
                  <div className="space-y-4">
                    {Array.isArray(viewTemplate.components) && viewTemplate.components.map((comp, i) => (
                      <div key={i} className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold capitalize">{comp.type.toLowerCase()}</h4>
                        {comp.format && <p><strong>Format:</strong> {comp.format}</p>}
                        {comp.text && <p><strong>Text:</strong> {comp.text}</p>}
                        {comp.image?.link && (
                          <div>
                            <strong>Image URL:</strong>{' '}
                            <a
                              href={comp.image.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 break-all"
                            >
                              {comp.image.link}
                            </a>
                          </div>
                        )}
                        {comp.parameters && Array.isArray(comp.parameters) && (
                          <div className="mt-2">
                            <strong>Parameters:</strong>
                            <ul className="list-disc list-inside">
                              {comp.parameters.map((p, idx) => (
                                <li key={idx}>{p.text}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end px-6 py-4 border-t">
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
